import { AnimatePresence, motion } from "framer-motion";

import {
  SMILE_SIMULATION_PROGRESS_CIRCLE_CIRCUMFERENCE,
  SMILE_SIMULATION_PROGRESS_CIRCLE_RADIUS,
} from "../model/constants";

type SimulationProgressOverlayProps = {
  isSimulationProcessing: boolean;
  simulationMessage: string;
  simulationProgress: number;
  simulationProgressLabel: string;
  simulationProgressOffset: number;
  variant?: "default" | "experience";
};

const EXPERIENCE_PROGRESS_STAGES = [
  {
    max: 20,
    label: "안면 해부학 구조 및 골격 맵핑 중",
  },
  {
    max: 40,
    label: "입술 라인과 치아의 황금 비율 산출 중",
  },
  {
    max: 60,
    label: "스마일 아치와 정중선 정밀 설계 중",
  },
  {
    max: 80,
    label: "치은 라인 및 주변부 심미 조화 매칭 중",
  },
  {
    max: 95,
    label: "자연광 반사를 고려한 치아 질감 렌더링 중",
  },
  {
    max: 100,
    label: "당신만을 위한 새로운 미소 디자인 완료",
  },
] as const;

const getExperienceProgressStage = (progress: number) =>
  EXPERIENCE_PROGRESS_STAGES.find(({ max }) => progress <= max) ??
  EXPERIENCE_PROGRESS_STAGES[5];

export function SimulationProgressOverlay({
  isSimulationProcessing,
  simulationMessage,
  simulationProgress,
  simulationProgressLabel,
  simulationProgressOffset,
  variant = "default",
}: SimulationProgressOverlayProps) {
  if (variant === "experience") {
    const stage = getExperienceProgressStage(simulationProgress);

    return (
      <div className="bg-[#161616]/62 absolute inset-0 flex flex-col items-center justify-center px-8 text-center text-white backdrop-blur-md">
        {isSimulationProcessing ? (
          <motion.div
            className="flex flex-col items-center gap-6"
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div className="relative">
              <svg
                width="156"
                height="156"
                viewBox="0 0 156 156"
                className="relative z-10 -rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx="78"
                  cy="78"
                  r="66"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="12"
                />
                <circle
                  cx="78"
                  cy="78"
                  r="66"
                  fill="none"
                  stroke="#8F14E5"
                  strokeWidth="12"
                  strokeDasharray={2 * Math.PI * 66}
                  strokeDashoffset={
                    2 * Math.PI * 66 * (1 - simulationProgress / 100)
                  }
                  className="transition-[stroke-dashoffset] duration-150 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[1.75rem] font-extrabold leading-none text-white">
                  {Math.round(simulationProgress)}%
                </span>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={stage.label}
                className="max-w-[22rem] text-[1.35rem] font-extrabold leading-snug text-white"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{
                  duration: 0.22,
                  ease: "easeOut",
                }}
              >
                {stage.label}
              </motion.p>
            </AnimatePresence>
          </motion.div>
        ) : (
          <p className="text-sm font-medium">{simulationMessage}</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[#13071f]/72 absolute inset-0 flex flex-col items-center justify-center px-8 text-center text-white">
      {isSimulationProcessing ? (
        <motion.div
          className="flex flex-col items-center gap-5"
          initial={{ opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.35,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <motion.div
            className="relative"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{
              duration: 2.6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="bg-[#8F14E5]/18 absolute inset-4 rounded-full blur-2xl" />
            <svg
              width="124"
              height="124"
              viewBox="0 0 124 124"
              className="relative z-10 -rotate-90"
              aria-hidden="true"
            >
              <circle
                cx="62"
                cy="62"
                r={SMILE_SIMULATION_PROGRESS_CIRCLE_RADIUS}
                fill="none"
                stroke="rgba(255,255,255,0.14)"
                strokeWidth="8"
              />
              <circle
                cx="62"
                cy="62"
                r={SMILE_SIMULATION_PROGRESS_CIRCLE_RADIUS}
                fill="none"
                stroke="#8F14E5"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={SMILE_SIMULATION_PROGRESS_CIRCLE_CIRCUMFERENCE}
                strokeDashoffset={simulationProgressOffset}
                className="transition-[stroke-dashoffset] duration-150 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-semibold tabular-nums tracking-tight text-white">
                {Math.round(simulationProgress)}%
              </span>
            </div>
          </motion.div>

          <div className="min-h-12">
            <AnimatePresence mode="wait">
              <motion.p
                key={simulationProgressLabel}
                className="text-sm font-medium text-white"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{
                  duration: 0.22,
                  ease: "easeOut",
                }}
              >
                {simulationProgressLabel}
              </motion.p>
            </AnimatePresence>
          </div>
        </motion.div>
      ) : (
        <p className="text-sm font-medium">{simulationMessage}</p>
      )}
    </div>
  );
}
